// src/api/energyApi.ts — client de l'agrégation ELEC source-aware (ts-ingest, process indépendant
// de report-api). Remplace les anciens appels /api/dataenr/{siteId}/... pour l'ELEC : ts-ingest
// n'a aucune connaissance de reports.db, donc chaque fonction reçoit la liste de PDL du site déjà
// résolue + taggée par source ("enedis"|"deepki") — cf. ElecMetersCtx dans ReportA4.tsx, qui fait
// cette résolution une fois par site via metersApi.listPdl(). Le GAZ a suivi le même chemin —
// cf. src/api/gazApi.ts — et n'appelle plus /api/dataenr non plus (supprimé de report-api, cf.
// CLAUDE.md, section "Migration ÉLEC + GAZ vers ts-ingest").

import { APP_CONFIG } from "../config/app.config";

const BASE = APP_CONFIG.tsIngestBaseUrl;

export type MeterSource = "enedis" | "deepki" | "engie";
export interface TaggedMeter { id: string; source: MeterSource; }
export interface UnsupportedMeter { id: string; reason: "pas-de-courbe-de-charge" | "pas-journalier-seul" | "pas-de-donnees-horaires"; }

async function tsRequest<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`);
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`ts-ingest ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

function metersParam(meters: TaggedMeter[]): string {
  return meters.map(m => `${m.id}:${m.source}`).join(",");
}

export function getMonthlyElec(meters: TaggedMeter[], years: number[]): Promise<{ consoElecTotaleParAnneeMois: Record<string, number> }> {
  const qs = new URLSearchParams({ meters: metersParam(meters), years: years.join("_") });
  return tsRequest(`/conso/mensuelle/elec?${qs}`);
}

export function getDailyElec(meters: TaggedMeter[], period: string): Promise<{ data: [number, number][] }> {
  const qs = new URLSearchParams({ meters: metersParam(meters), period });
  return tsRequest(`/conso/journaliere/elec?${qs}`);
}

export interface CdcDetailResponse {
  step_h: number | null;
  totalExpectedPerDay: number | null;
  pdlCount: number;
  cdcPointsManquants: number;
  cdcCounts: Record<string, number>;
  perPdl: Array<{ id: string; cdcPointsManquants: number; missingSlots: Array<{ day: string; missing: number; total: number; ranges: string[] }> }>;
  unsupported: UnsupportedMeter[];
}

export function getCdcDetail(meters: TaggedMeter[], period: string): Promise<CdcDetailResponse> {
  const qs = new URLSearchParams({ meters: metersParam(meters), period });
  return tsRequest(`/consocdc/detail?${qs}`);
}

export function getAvgDay(meters: TaggedMeter[], period: string): Promise<{ data: number[]; unsupported: UnsupportedMeter[] }> {
  const qs = new URLSearchParams({ meters: metersParam(meters), period });
  return tsRequest(`/conso/journee_type?${qs}`);
}

export function getAvgDayPerMonth(meters: TaggedMeter[], period: string): Promise<{ data: Record<string, number[]>; unsupported: UnsupportedMeter[] }> {
  const qs = new URLSearchParams({ meters: metersParam(meters), period });
  return tsRequest(`/conso/journee_type_mois?${qs}`);
}

export function getAvgWeek(meters: TaggedMeter[], period: string, debug?: boolean): Promise<{ data: number[][]; unsupported: UnsupportedMeter[] }> {
  const qs = new URLSearchParams({ meters: metersParam(meters), period, ...(debug ? { debug: "true" } : {}) });
  return tsRequest(`/conso/semaine_type?${qs}`);
}

export function getMissingDaysElec(meters: TaggedMeter[], from: string, to: string): Promise<{ missing: { from: string; to: string }[]; totalDays: number; missingDays: number; coverage: number | null }> {
  const qs = new URLSearchParams({ meters: metersParam(meters), from, to });
  return tsRequest(`/manquantes/elec?${qs}`);
}
